<template>
    <div class="chart js-chart" style="width:100%; min-width: 0; height:544px;"></div>
</template>

<style>
    .container {
        position: relative;
    }
</style>

<script>
    import * as Highcharts from 'highcharts'
    import * as HighchartsNoDataDisplay from 'highcharts/modules/no-data-to-display'
    import  * as HighchartsFunnel from 'highcharts/modules/funnel'
    import {util} from 'vue'
    import {defaultChartConfig} from './chart-config'

    HighchartsNoDataDisplay(Highcharts);
    HighchartsFunnel(Highcharts);

    export default {
        props: {
            initChartOptions: {
                type: Object,
                default: function () {
                    return {}
                },
                required: false
            },
            initChartData: {
                type: String,
                default: null,
                required: true
            },
            initChartType: {
                type: String,
                default: 'line',
                required: true
            },
            initSrcUrl: {
                type: String,
                required: false
            }
        },
        data() {
            return {
                chartConfig: {},
                chart: null,
                chartData: this.initChartData,
                chartType: this.initChartType
            }
        },
        mounted() {
            if (!this.getChart()) {
                this.init();
            }
        },
        beforeDestroy() {
            if (this.getChart()) {
                this.getChart().destroy();
            }
        },
        methods: {
            getChart() {
                return this.chart;
            },
            exportChart() {
                this.chart.exportChart({
                    type: "application/pdf"
                });
            },
            setupChart() {
                let self = this,
                    chartType = /^stock/i.test(this.chartType) ? 'stock' : _.camelCase(this.chartType),
                    chartDataObj = JSON.parse(this.chartData);

                // Set up config common properties and certaina chart properties
                $.extend(
                    true,
                    this.chartConfig,
                    defaultChartConfig.common,
                    defaultChartConfig[chartType]
                );

                // Set up 'series' and categories of 'xaxis' and redefine config by initialization
                if (chartType !== 'donut' && chartType !== 'funnel') {
                    $.extend(
                        true,
                        this.chartConfig, {
                            xAxis: {
                                categories: chartDataObj.xAxisCategories,
                                title: {
                                    text: chartDataObj.xAxisTitle
                                }
                            },
                            yAxis: {
                                title: {
                                    text: chartDataObj.yAxisTitle
                                }
                            },
                            tooltip: {
                                formatter: function () {
                                    var yFormatted = this.y.toLocaleString('de-DE'),
                                        xLabelStr = 'im ' + this.x,
                                        seriesName = this.series.name;

                                    return self.getTooltipTemplate(yFormatted, seriesName, xLabelStr);
                                }
                            }
                        });

                    if (chartType == 'stock') {
                        let chartTypeStringSplit = this.chartType.split("_"),
                            chartTypeSubString = chartTypeStringSplit.slice(-1)[0],
                            stockChartType = /^stock/i.test(chartTypeSubString) ? 'line' : chartTypeSubString,
                            xAxisCategories = chartDataObj.xAxisCategories,
                            arrTimeStamp = [+moment().hour('00').minute('00').second('00').millisecond('00')];

                        this.prepareDataForStockCharts(chartDataObj, arrTimeStamp);

                        $.extend(
                            true,
                            this.chartConfig, {
                                chart: {
                                    type: stockChartType
                                },
                                xAxis: {
                                    labels: {
                                        formatter: function () {
                                            var index = arrTimeStamp.indexOf(this.value);
                                            return xAxisCategories[index];
                                        }
                                    },
                                    min: +moment(arrTimeStamp[0]).add(-12, 'h').format('x'),
                                    max: +moment(arrTimeStamp[arrTimeStamp.length - 1]).add(12, 'h').format('x')
                                },
                                tooltip: {
                                    formatter: function () {
                                        var yFormatted = this.y,
                                            index = this.series.hasOwnProperty('xData') ? this.series.xData.indexOf(this.x) : null,
                                            xLabelStr = index ? 'im ' + xAxisCategories[index] : '',
                                            seriesName = this.series.name;

                                        return self.getTooltipTemplate(yFormatted, seriesName, xLabelStr);
                                    }
                                },
                                navigator: {
                                    xAxis: {
                                        labels: {
                                            formatter: function () {
                                                var index = arrTimeStamp.indexOf(this.value);
                                                return xAxisCategories[index];
                                            }
                                        }
                                    }
                                },
                            });

                        if (chartTypeStringSplit[1] == 'stacked') {
                            $.extend(
                                true,
                                this.chartConfig, {
                                    plotOptions: {
                                        column: {
                                            borderWidth: 0,
                                            stacking: 'normal',
                                            dataLabels: {
                                                enabled: false
                                            }
                                        }
                                    }
                                });
                        }
                    }
                } else {
                    let period = chartDataObj.period,
                        startDate = period.start_date ? moment(period.start_date).format('DD.MM.YYYY') : '',
                        endDate = period.end_date ? moment(period.end_date).format('DD.MM.YYYY') : '',
                        periodTooltipStr = startDate + ' - ' + endDate;

                    // Sort data for donut chart
                    if (chartType == 'donut') {
                        let data = chartDataObj.series[0].data;
                        let dataLength = data.length;
                        let colors = this.configureColors(dataLength);

                        this.chartConfig.colors = colors;

                        data.sort(function compare(a, b) {
                            if (a.y < b.y)
                                return -1;
                            if (a.y > b.y)
                                return 1;
                            return 0;
                        });
                    }

                    $.extend(
                        true,
                        this.chartConfig,
                        {
                            tooltip: {
                                formatter: function () {
                                    return self.getTooltipTemplate(this.y, this.key, periodTooltipStr);
                                }
                            }
                        },
                        this.initChartOptions
                    );

                }

                $.extend(
                    true,
                    this.chartConfig,
                    {
                        series: chartDataObj.series,
                        exporting: this.configureExport()
                    },
                    this.initChartOptions
                );
            },
            prepareDataForStockCharts(chartDataObj, arrTimeStamp) {
                let xAxisCategories = chartDataObj.xAxisCategories,
                    series = chartDataObj.series,
                    dataLength = xAxisCategories.length;

                for (let i = 0; i < dataLength - 1; i++) {
                    let currentTimeStamp = arrTimeStamp[i];
                    let newTimeStamp = moment(currentTimeStamp).add(1, 'days').format('x');
                    arrTimeStamp.push(+newTimeStamp);
                }

                for (let s of series) {
                    for (let d = 0; d < dataLength; d++) {
                        let currentSerieData = s.data;
                        currentSerieData[d] = [arrTimeStamp[d], currentSerieData[d]];
                    }
                }
            },
            init() {
                this.setupChart();

                if (!this.getChart()) {
                    if (/^stock/i.test(this.chartType)) {
                        Highcharts.setOptions({
                            global: {
                                useUTC: false
                            },
                            lang: {
                                numericSymbols: ['k', 'M', 'G', 'T', 'P', 'E']
                            }
                        });
                        this.chart = Highcharts.stockChart(this.$el, $.extend({}, this.chartConfig));
                    } else {
                        this.chart = Highcharts.chart(this.$el, this.chartConfig);
                    }

                    this.$emit('chart-init')
                }
            },
            fetch() {
                if (!this.srcUrl) {
                    return Promise.reject(new Error('Source URL is not defined.'));
                }
                return axios.get(this.srcUrl);
            },
            reflowChart() {
                if (this.getChart()) {
                    this.chart.reflow();
                }
            },
            blendColors(c0, c1, p) {
                // https://stackoverflow.com/questions/5560248/programmatically-lighten-or-darken-a-hex-color-or-rgb-and-blend-colors
                var f = parseInt(c0.slice(1), 16),
                    t = parseInt(c1.slice(1), 16),
                    R1 = f >> 16,
                    G1 = f >> 8 & 0x00FF,
                    B1 = f & 0x0000FF,
                    R2 = t >> 16,
                    G2 = t >> 8 & 0x00FF,
                    B2 = t & 0x0000FF;

                return "#" + (0x1000000 + (Math.round((R2 - R1) * p) + R1) * 0x10000 + (Math.round((G2 - G1) * p) + G1) * 0x100 + (Math.round((B2 - B1) * p) + B1)).toString(16).slice(1);
            },
            configureColors(dataLength) {
                // colors #28c59f, #03ace0, #0068e0, #0b2e7d
                let startColor = '#28c59f';
                let endColor = '#0b2e7d';
                let colors = [startColor, endColor];
                let step = Math.floor((1 / dataLength) * 100) / 100;

                if (dataLength > 2) {
                    for (let i = 1; i < (dataLength - 1); i++) {
                        let stepColor = this.blendColors(startColor, endColor, step * i);
                        colors[i] = stepColor;
                    }

                    colors[dataLength - 1] = endColor;
                }

                return colors;
            },
            configureExport() {
                return {
                    buttons: {
                        contextButton: {
                            enabled: false
                        }
                    }
                };
            },
            getTooltipTemplate(value, name, label) {
                return `<span>
                        <table class="brocom-highcharts-tooltip">
                        <tr>
                        <td>
                        <span class="brocom-highcharts-tooltip-value">${value}</span>
                        </td>
                        <td>
                        <span class="brocom-highcharts-tooltip-series-name">${name}</span><br />
                        <span class="brocom-highcharts-tooltip-label">${label}</span>
                        </td>
                        </tr>
                        </table>
                        </span>`;
            }
        }
    }
</script>



// WEBPACK FOOTER //
// resources/assets/js/components/Chart.vue